import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import config from '../config';
import '../styles/boostProduct.css';
import Notification from '../components/Notification';
import { getMediaUrl } from '../utils/urlHelpers';
import { FaRocket, FaWallet, FaCreditCard } from 'react-icons/fa';
import { BoostProductSkeleton } from '../components/Skeletons';
import { UserContext } from '../contexts/UserContext';

const BoostProduct = () => {
    const { productUuid } = useParams();  // Changed from id to productUuid
    const navigate = useNavigate();
    const { redirectToLogin } = useContext(UserContext);
    
    const [productData, setProductData] = useState(null);
    const [productLoading, setProductLoading] = useState(true);
    const [userBalance, setUserBalance] = useState(0);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });

    // Boost packages with attractive tiered pricing to encourage bulk purchases
    const boostPackages = [
        {
            id: 1,
            name: "Boost Simple",
            price: 0.5,
            boosts: 1,
            description: "Un boost supplémentaire pour remonter votre produit",
            originalPrice: 0.5,
        },
        {
            id: 2,
            name: "Pack Boost",
            price: 1.2, // Save 0.3 Zaires (20% discount)
            boosts: 3,
            description: "Trois boosts pour une visibilité prolongée - Économisez 20%",
            popular: true,
            originalPrice: 1.5,
            savings: 0.3,
            discount: "20%"
        },
        {
            id: 3,
            name: "Mega Boost",
            price: 2.0, // Save 1.0 Zaires (33% discount)
            boosts: 6,
            description: "Six boosts pour une visibilité maximale - Économisez 33%",
            originalPrice: 3.0,
            savings: 1.0,
            discount: "33%"
        },
    ];

    useEffect(() => {

        fetchProductData();
        fetchUserBalance();
    }, [productUuid]);  // Changed from id to productUuid

    const fetchProductData = async () => {
        try {

            const token = Cookies.get('authToken');
            if (!token) {

                redirectToLogin(window.location.pathname);
                return;
            }

            const url = `${config.backendUrl}/products/view/${productUuid}/`;  // Changed from id to productUuid

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Token ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();

                setProductData(data);
            } else {
                console.error('BoostProduct: Failed to fetch product, status:', response.status);
                throw new Error('Erreur lors du chargement du produit');
            }
        } catch (error) {
            console.error('BoostProduct: Error fetching product:', error);
            showNotification('Erreur lors du chargement du produit', 'error', 'Erreur');
            // Don't navigate back immediately, let user try again
        } finally {
            setProductLoading(false);
        }
    };

    const fetchUserBalance = async () => {
        try {
            const token = Cookies.get('authToken');
            if (!token) return;

            const response = await fetch(`${config.backendUrl}/payments/wallet/`, {
                headers: {
                    'Authorization': `Token ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                const balance = parseFloat(data.balance) || 0;
                setUserBalance(balance);
            }
        } catch (error) {
            console.error('Error fetching balance:', error);
        } finally {
            setBalanceLoading(false);
        }
    };

    const showNotification = (message, type, header, options = {}) => {
        setNotification({
            message,
            type,
            header,
            ...options
        });
    };

    const handlePurchaseBoosts = async () => {
        if (!selectedPackage) {
            showNotification('Veuillez sélectionner un package de boost', 'error', 'Erreur');
            return;
        }

        if (userBalance < selectedPackage.price) {
            showNotification(
                `Solde insuffisant. Vous avez besoin de ${selectedPackage.price} Zaires. Votre solde actuel est de ${userBalance} Zaires.`,
                'error',
                'Solde Insuffisant',
                {
                    showButtons: true,
                    onConfirm: () => navigate('/wallet'),
                    confirmText: 'Recharger le Portefeuille'
                }
            );
            return;
        }

        setLoading(true);
        
        // Final balance check before API call - same as mobile app
        if (userBalance < selectedPackage.price) {
            showNotification('Solde insuffisant pour ce boost.', 'error', 'Solde insuffisant');
            setLoading(false);
            return;
        }
        
        try {
            const token = Cookies.get('authToken');
            
            // Use new quantity-based API for more efficient processing
            const response = await fetch(`${config.backendUrl}/products/boost-product/${productUuid}/`, {  // Changed from id to productUuid
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({
                    quantity: selectedPackage.boosts,
                    total_amount: selectedPackage.price,
                    package_name: selectedPackage.name
                })
            });

            if (response.ok) {
                const data = await response.json();
                // Refresh user balance
                await fetchUserBalance();
                
                showNotification(
                    `${data.quantity_applied} boost(s) appliqué(s) avec succès!`,
                    'success',
                    'Boosts Appliqués',
                    {
                        onClose: () => navigate(`/products/view/${productUuid}`)  // Changed from product-details to products/view and use productUuid
                    }
                );
            } else {
                const errorData = await response.json();
                showNotification(errorData.error || 'Erreur lors de l\'application du boost', 'error', 'Erreur');
            }
        } catch (error) {
            console.error('Error processing boosts:', error);
            showNotification('Erreur lors du traitement des boosts', 'error', 'Erreur');
        } finally {
            setLoading(false);
        }
    };

    if (productLoading || balanceLoading) {
        return <BoostProductSkeleton />;
    }

    if (!productData) {
        return (
            <div className="boost-product-page">
                <div className="error-container">
                    <h2>Produit non trouvé</h2>
                    <button onClick={() => navigate(-1)} className="back-button">
                        Retour
                    </button>
                </div>
            </div>
        );
    }

    const currentBoosts = productData.booster || 0;
    const freeBoostsRemaining = Math.max(0, 3 - currentBoosts);

    return (
        <div className="boost-product-page">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                    onConfirm={notification.onConfirm}
                    showButtons={notification.showButtons}
                    confirmText={notification.confirmText}
                />
            )}

            <div className="boost-container">
                <div className="boost-header">
                    <div className="header-content">
                        <FaRocket className="boost-icon" />
                        <h1>Booster le Produit</h1>
                    </div>
                </div>

                <div className="product-preview-section">
                    <div className="section-header">
                        <h2>Produit à Booster</h2>
                    </div>
                    <div className="boost-product-card">
                        <div className="boost-product-preview">
                            <div className="boost-product-image-container">
                                {productData.media && productData.media.length > 0 ? (
                                    <img 
                                        src={getMediaUrl(productData.media[0].url || productData.media[0].image)} 
                                        alt={productData.name} 
                                        className="boost-product-image"
                                        onError={(e) => {
                                            console.error('Image failed to load:', e.target.src);
                                            e.target.onerror = null;
                                            e.target.src = "https://via.placeholder.com/300x300?text=Image+non+disponible";
                                        }}
                                    />
                                ) : (
                                    <div className="boost-product-image-placeholder">
                                        <i className="fas fa-image"></i>
                                        <span>Image non disponible</span>
                                    </div>
                                )}
                                <div className="boost-product-badge">
                                    <FaRocket />
                                    <span>{currentBoosts}</span>
                                </div>
                            </div>
                            <div className="boost-product-details">
                                <div className="boost-product-header">
                                    <h3 className="boost-product-name">{productData.name}</h3>
                                    <div className="boost-product-price-section">
                                        <div className="boost-product-price-container">
                                            <span className="boost-product-price-label">Prix:</span>
                                            <span className="boost-product-price-value">
                                                {productData.currency} {productData.price 
                                                    ? parseFloat(productData.price).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                                                    : 'Non disponible'}
                                            </span>
                                        </div>
                                        <div className="boost-product-boost-count">
                                            <i className="fas fa-rocket"></i>
                                            <span>{currentBoosts} boost{currentBoosts > 1 ? 's' : ''}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="boost-product-attributes">
                                    <div className="boost-attribute-item">
                                        <span className="boost-attribute-label">Catégorie</span>
                                        <div className="boost-attribute-content">
                                            <i className="fas fa-tag boost-attribute-icon"></i>
                                            <span className="boost-attribute-value">{productData.category_name || 'Non spécifiée'}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="boost-attribute-item">
                                        <span className="boost-attribute-label">État</span>
                                        <div className="boost-attribute-content">
                                            <i className="fas fa-box boost-attribute-icon"></i>
                                            <span className="boost-attribute-value">{productData.condition || 'Non spécifié'}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="boost-attribute-item">
                                        <span className="boost-attribute-label">Ville</span>
                                        <div className="boost-attribute-content">
                                            <i className="fas fa-map-marker-alt boost-attribute-icon"></i>
                                            <span className="boost-attribute-value">{productData.city_name || 'Non spécifiée'}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="boost-attribute-item">
                                        <span className="boost-attribute-label">Date</span>
                                        <div className="boost-attribute-content">
                                            <i className="fas fa-calendar-alt boost-attribute-icon"></i>
                                            <span className="boost-attribute-value">
                                                {productData.date_created ? new Date(productData.date_created).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: '2-digit'}) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="boost-packages">
                    <h2>Choisissez votre package de boost</h2>
                    <div className="packages-grid">
                        {boostPackages.map((pkg) => (
                            <div
                                key={pkg.id}
                                className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}
                                onClick={() => setSelectedPackage(pkg)}
                            >
                                {pkg.popular && <div className="popular-badge">Plus Populaire</div>}
                                {pkg.discount && <div className="discount-badge">-{pkg.discount}</div>}
                                <h3>{pkg.name}</h3>
                                <div className="package-pricing">
                                    <div className="package-price">{pkg.price} Zaires</div>
                                    {pkg.originalPrice && pkg.originalPrice > pkg.price && (
                                        <div className="original-price">{pkg.originalPrice} Zaires</div>
                                    )}
                                </div>
                                <div className="package-boosts">{pkg.boosts} Boost{pkg.boosts > 1 ? 's' : ''}</div>
                                <p className="package-description">{pkg.description}</p>
                                <div className="package-value">
                                    {pkg.savings && (
                                        <span className="savings">
                                            Économisez {pkg.savings.toFixed(1)} Zaires!
                                        </span>
                                    )}
                                    {!pkg.savings && pkg.boosts === 1 && (
                                        <span className="standard-price">
                                            Prix standard
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="wallet-info">
                    <div className="wallet-balance">
                        <FaWallet />
                        <span>Votre solde: {(typeof userBalance === 'number' ? userBalance : parseFloat(userBalance) || 0).toFixed(2)} Zaires</span>
                    </div>
                    
                    {selectedPackage && userBalance < selectedPackage.price && (
                        <div className="insufficient-balance">
                            <p>Solde insuffisant pour ce package</p>
                            <button 
                                onClick={() => navigate('/wallet')}
                                className="topup-button"
                            >
                                <FaCreditCard /> Recharger le Portefeuille
                            </button>
                        </div>
                    )}
                </div>

                <div className="boost-actions">
                    <div className="form-actions">
                        <button
                            onClick={handlePurchaseBoosts}
                            disabled={loading || !selectedPackage || (selectedPackage && userBalance < selectedPackage.price)}
                            className="boost-product-submit-button"
                        >
                            {loading ? (
                                'Traitement...'
                            ) : (
                                selectedPackage 
                                    ? `Acheter ${selectedPackage.boosts} Boost${selectedPackage.boosts > 1 ? 's' : ''} - ${selectedPackage.price} Zaires`
                                    : 'Sélectionnez un package'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BoostProduct;
